import { IEventDataSource, IGameOrganizer } from "@/types/interfaces";
import { BigBashEvent, Game, RoomName } from "@/types/types";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, PropertyValues, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getChangedGameOrderPositions } from "./gameOrderMergeHandler";

const ROW_HIGHLIGHT_DURATION_MS = 1_000;

@customElement("moderate-event-table")
export class ModerateEventTable extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			.events-table {
				display: flex;
				flex-direction: column;
				gap: 8px;
				padding: 8px;
			}
			.table-header {
				display: flex;
				padding: 8px;
				background: #f8f9fa;
			}
			.table-body {
				display: flex;
				flex-direction: column;
				gap: 16px;
			}
			.table-cell {
				flex: 1;
			}
			.table-cell--aktionen {
				flex: 1.5;
			}
			.room-section {
				display: flex;
				flex-direction: column;
				gap: 8px;
			}
			.room-section-header {
				padding: 12px 8px;
				background: #e9ecef;
				border-radius: 4px;
				font-weight: 600;
				font-family: var(--font-text);
			}
			.room-section-header--skills {
				background: #d4edda;
				color: #155724;
			}
			.room-section-header--brain {
				background: #cce5ff;
				color: #004085;
			}
			.room-section-header--action {
				background: #f8d7da;
				color: #721c24;
			}
		`
	];

	@property({ type: Object }) event: BigBashEvent;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	@property({ type: Array }) allGames: Game[] = [];
	@property({ type: Object }) gameOrganizer: IGameOrganizer;
	@state() private recentlyUpdatedPositions: Set<string> = new Set();

	constructor(event: BigBashEvent, eventDataSource: IEventDataSource, allGames: Game[], gameOrganizer: IGameOrganizer) {
		super();
		this.event = event;
		this.eventDataSource = eventDataSource;
		this.allGames = allGames;
		this.gameOrganizer = gameOrganizer;
	}

	private get gamesInPlayOrder(): Game[] {
		return this.gameOrganizer.returnGamesInOrder(this.event.game_order, this.event.games);
	}

	private get gamesSectionedByRoom(): { room: RoomName; games: Game[] }[] {
		const gamesInOrder = this.gamesInPlayOrder;
		return this.gameOrganizer.getGamesSectioned(gamesInOrder);
	}

	private _getGamesForRoom(roomId: string, currentGameId: string): Game[] {
		const gamesForRoom = this.allGames.filter(game => game.default_room === roomId);
		const usedGameIds = this.event.games.map(game => game.id);
		return gamesForRoom.filter(game =>
			!usedGameIds.includes(game.id) || game.id === currentGameId
		);
	}

	private _hasGameBeenPlayed(gameId: string): boolean {
		return this.event.game_points.some(gp => gp.game === gameId);
	}

	private _getRoomDisplayName(room: RoomName): string {
		switch (room) {
			case RoomName.skills: return '🎯 Skills';
			case RoomName.brain: return '🧠 Brain';
			case RoomName.action: return '🏃 Action';
			default: return room;
		}
	}

	private _getRoomHeaderClass(room: RoomName): string {
		switch (room) {
			case RoomName.skills:
				return 'room-section-header room-section-header--skills';
			case RoomName.brain:
				return 'room-section-header room-section-header--brain';
			case RoomName.action:
				return 'room-section-header room-section-header--action';
			default:
				return 'room-section-header';
		}
	}

	private _getOriginalIndex(game: Game): number {
		return this.gamesInPlayOrder.findIndex(g => g.id === game.id);
	}

	protected willUpdate(changedProperties: PropertyValues): void {
		const previousEvent = changedProperties.get('event') as BigBashEvent | undefined;
		if (!previousEvent) {
			return;
		}
		const changedPositions = getChangedGameOrderPositions(previousEvent.game_order, this.event.game_order);
		this._highlightRows(changedPositions);
	}

	private _highlightRows(positions: string[]): void {
		if (positions.length === 0) {
			return;
		}
		this.recentlyUpdatedPositions = new Set([...Array.from(this.recentlyUpdatedPositions), ...positions]);
		setTimeout((): void => {
			const stillHighlighted = new Set(this.recentlyUpdatedPositions);
			positions.forEach(position => stillHighlighted.delete(position));
			this.recentlyUpdatedPositions = stillHighlighted;
		}, ROW_HIGHLIGHT_DURATION_MS);
	}

	render() {
		return html`
			<div class="events-table">
				<div class="table-header">
					<div class="table-cell text-body--small">Spiel</div>
					<div class="table-cell text-body--small">Zählsystem</div>
					<div class="table-cell table-cell--aktionen text-body--small">Aktionen</div>
				</div>
				<div class="table-body">
					${this.gamesSectionedByRoom.map(section => html`
						<div class="room-section">
							<div class="${this._getRoomHeaderClass(section.room)}">
								${this._getRoomDisplayName(section.room)}
							</div>
							${section.games.map(g => html`
								<moderate-event-table-row
									.rowIndex=${this._getOriginalIndex(g).toString()}
									.gamesForThisRoom=${this._getGamesForRoom(g.default_room, g.id)}
									.isStartButtonDisabled=${g.name === ""}
									.game=${g}
									.eventId=${this.event.id}
									.hasBeenPlayed=${this._hasGameBeenPlayed(g.id)}
									.eventDataSource=${this.eventDataSource}
									.highlight=${this.recentlyUpdatedPositions.has(this._getOriginalIndex(g).toString())}
								></moderate-event-table-row>
							`)}
						</div>
					`)}
				</div>
			</div>
		`;
	}
}

export default ModerateEventTable;